import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { IpeBrandLogo, IpeBrandMark } from './IpeBrandLogo';

const LOGIN_IMAGE_BASE = '/images/ipe/login';

const COMPARE_TABS = [
  {
    id: 'goods',
    label: '主图创作',
    traditional: {
      time: '50 mins',
      steps: [
        '登录 PS / 在线设计工具，按平台规范新建画布',
        '从本地文件夹逐张找图、上传素材并重命名对齐 SKU',
        '手动裁剪主图比例，逐张调整亮度、构图与细节',
        '拖拽排版详情页模块，对齐间距并反复微调位置',
        '多套模版逐个套用，分别导出 JPG / PNG 多版本',
        '各平台尺寸再手工另存一遍，逐张核对有无裁切',
      ],
      footnote: '商品批量上新时，效率极低',
    },
    ai: {
      time: '5 mins',
      previewType: 'gallery',
      actionLabel: '一键生成套图',
      badge: '6 张套图已就绪',
      images: Array.from({ length: 6 }, (_, i) => `${LOGIN_IMAGE_BASE}/compare-gallery-${i + 1}.png`),
    },
  },
  {
    id: 'crop',
    label: '智能抠图',
    traditional: {
      time: '45 mins',
      steps: [
        '从网盘 / 邮件下载原图，解压并按 SKU 整理到本地',
        '在 PS 中用钢笔 / 魔棒逐张框选商品主体',
        '放大至 400% 手工修边，处理发丝与半透明区域',
        '边缘羽化、阴影单独微调，逐张对比前后效果',
        '导出透明 PNG，按命名规则归档并回传共享文件夹',
        '漏抠或毛边返工，同一批图片反复打开调整',
      ],
      footnote: '大量 SKU 处理时，人力成本极高',
    },
    ai: {
      time: '3 mins',
      previewType: 'cutout',
      actionLabel: '立即智能抠图',
      beforeImage: `${LOGIN_IMAGE_BASE}/compare-cutout-before.png`,
      afterImage: `${LOGIN_IMAGE_BASE}/compare-cutout-after.png`,
      stat: '主体识别 98%',
    },
  },
  {
    id: 'title',
    label: '标题提取',
    traditional: {
      time: '35 mins',
      steps: [
        '逐个打开竞品 / 货源详情页，切换窗口对照查看',
        '复制标题、卖点、参数，粘贴到 Excel 分栏整理',
        '按各平台字数限制与违禁词规则手工删改润色',
        '多语言版本分别翻译、校对，再分段调整格式',
        '对照商品图核对颜色、尺码、规格是否填写一致',
        '逐条录入 ERP / 店铺后台，重复检查标点与字段',
      ],
      footnote: '多平台文案格式不一，重复劳动多',
    },
    ai: {
      time: '2 mins',
      previewType: 'title',
      actionLabel: '提取商品标题',
      sourceImage: `${LOGIN_IMAGE_BASE}/compare-title-source.png`,
      titles: ['夏季透气运动休闲鞋 男女同款', '轻便跑步鞋 减震防滑', '多色可选 39-44 码'],
    },
  },
  {
    id: 'listing',
    label: '批量上架',
    traditional: {
      time: '90 mins',
      steps: [
        '分别登录多个店铺后台，切换账号并完成验证',
        '新建商品链接，逐字段填写标题、类目与属性',
        '本地上传主图 / 详情图，等待每张上传与审核通过',
        '手动配置 SKU、库存、价格、运费与促销规则',
        '每个链接预览检查排版、图片顺序与文案错字',
        '多店重复以上全流程，任一字段填错则整批返工',
      ],
      footnote: '多店铺重复上架，耗时且易出错',
    },
    ai: {
      time: '8 mins',
      previewType: 'batch',
      actionLabel: '开始批量上架',
      progress: 86,
      items: [
        { name: '极简风陶瓷马克杯', status: 'done' },
        { name: '法式碎花连衣裙', status: 'done' },
        { name: '无线蓝牙耳机', status: 'running' },
      ],
    },
  },
  {
    id: 'platform',
    label: '上架管理',
    traditional: {
      time: '55 mins',
      steps: [
        '从 A 平台导出 Excel / CSV 商品、库存与价格表',
        '对照 B 平台字段说明，手工改列名、编码与单位格式',
        '库存、价格、属性逐行复制粘贴，避免错位与漏填',
        '图片链接批量替换域名，逐条打开检查是否失效',
        '各店铺分别登录，确认同步状态并记录差异项',
        '发现不一致再逐条排查、补录、重新对账确认',
      ],
      footnote: '跨平台数据不一致，维护成本高',
    },
    ai: {
      time: '6 mins',
      previewType: 'sync',
      actionLabel: '同步店铺数据',
      platforms: [
        { name: '货源平台', status: '已连接', color: '#ff6a00' },
        { name: '本地商品库', status: '已同步', color: '#0052ff' },
        { name: '上架任务', status: '进行中', color: '#6366f1' },
      ],
    },
  },
];

function PreviewShell({ ai, onAction, className, children }) {
  return (
    <div className={`ipe-login-compare__preview${className ? ` ${className}` : ''}`}>
      <div className="ipe-login-compare__preview-body">{children}</div>
      <button type="button" className="ipe-login-compare__action-btn" onClick={onAction}>
        {ai.actionLabel}
      </button>
    </div>
  );
}

function CompareExpandOverlay({ src, checkerboard = false }) {
  if (!src) return null;

  return (
    <div
      className={`ipe-login-compare__expand${checkerboard ? ' ipe-login-compare__expand--checker' : ''}`}
      aria-hidden="true"
    >
      <img className="ipe-login-compare__expand-image" src={src} alt="" draggable={false} />
    </div>
  );
}

function GalleryPreview({ ai, onAction }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const expanded = hoveredIndex !== null;

  return (
    <PreviewShell ai={ai} onAction={onAction} className="ipe-login-compare__preview--gallery">
      <div
        className={`ipe-login-compare__stage${expanded ? ' ipe-login-compare__stage--expanded' : ''}`}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <div className="ipe-login-compare__gallery-grid">
          {ai.images.map((src, i) => (
            <span
              key={`gallery-${i}`}
              className="ipe-login-compare__gallery-item"
              onMouseEnter={() => setHoveredIndex(i)}
            >
              <img
                className="ipe-login-compare__gallery-image"
                src={src}
                alt=""
                loading="lazy"
                draggable={false}
              />
            </span>
          ))}
        </div>
        <span className="ipe-login-compare__preview-badge">{ai.badge}</span>
        {expanded ? <CompareExpandOverlay src={ai.images[hoveredIndex]} /> : null}
      </div>
    </PreviewShell>
  );
}

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function CutoutPreview({ ai, onAction }) {
  const { t } = useTranslation('login');
  const [hoveredSide, setHoveredSide] = useState(null);
  const expanded = hoveredSide !== null;
  const expandSrc = hoveredSide === 'before' ? ai.beforeImage : ai.afterImage;

  return (
    <PreviewShell ai={ai} onAction={onAction} className="ipe-login-compare__preview--cutout">
      <div
        className={`ipe-login-compare__stage${expanded ? ' ipe-login-compare__stage--expanded' : ''}`}
        onMouseLeave={() => setHoveredSide(null)}
      >
        <div className="ipe-login-compare__cutout-pair">
          <div className="ipe-login-compare__cutout-pane">
            <span className="ipe-login-compare__cutout-label">{t('compare.original')}</span>
            <div
              className="ipe-login-compare__cutout-image"
              onMouseEnter={() => setHoveredSide('before')}
            >
              <img
                className="ipe-login-compare__cutout-photo"
                src={ai.beforeImage}
                alt=""
                loading="lazy"
                draggable={false}
              />
            </div>
          </div>
          <div className="ipe-login-compare__cutout-arrow" aria-hidden="true">→</div>
          <div className="ipe-login-compare__cutout-pane">
            <span className="ipe-login-compare__cutout-label">{t('compare.cutout')}</span>
            <div
              className="ipe-login-compare__cutout-image ipe-login-compare__cutout-image--clear"
              onMouseEnter={() => setHoveredSide('after')}
            >
              <img
                className="ipe-login-compare__cutout-photo"
                src={ai.afterImage}
                alt=""
                loading="lazy"
                draggable={false}
              />
            </div>
          </div>
        </div>
        <span className="ipe-login-compare__preview-stat">{ai.stat}</span>
        {expanded ? (
          <CompareExpandOverlay src={expandSrc} checkerboard={hoveredSide === 'after'} />
        ) : null}
      </div>
    </PreviewShell>
  );
}

function TitlePreview({ ai, onAction }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <PreviewShell ai={ai} onAction={onAction} className="ipe-login-compare__preview--title">
      <div
        className={`ipe-login-compare__stage${expanded ? ' ipe-login-compare__stage--expanded' : ''}`}
        onMouseLeave={() => setExpanded(false)}
      >
        <div
          className="ipe-login-compare__title-source"
          onMouseEnter={() => setExpanded(true)}
        >
          <img
            className="ipe-login-compare__title-source-image"
            src={ai.sourceImage}
            alt=""
            loading="lazy"
            draggable={false}
          />
        </div>
        <div className="ipe-login-compare__title-results">
          {ai.titles.map((text, i) => (
            <p key={text} className={i === 0 ? 'ipe-login-compare__title-line ipe-login-compare__title-line--primary' : 'ipe-login-compare__title-line'}>
              {i === 0 ? '✓ ' : ''}{text}
            </p>
          ))}
        </div>
        {expanded ? <CompareExpandOverlay src={ai.sourceImage} /> : null}
      </div>
    </PreviewShell>
  );
}

function AiPreview({ ai, onAction }) {
  const { t } = useTranslation('login');
  switch (ai.previewType) {
    case 'gallery':
      return <GalleryPreview ai={ai} onAction={onAction} />;

    case 'cutout':
      return <CutoutPreview ai={ai} onAction={onAction} />;

    case 'title':
      return <TitlePreview ai={ai} onAction={onAction} />;

    case 'batch':
      return (
        <PreviewShell ai={ai} onAction={onAction} className="ipe-login-compare__preview--batch">
          <ul className="ipe-login-compare__batch-list">
            {ai.items.map((item) => (
              <li key={item.name} className={`ipe-login-compare__batch-item ipe-login-compare__batch-item--${item.status}`}>
                <span className="ipe-login-compare__batch-dot" />
                <span className="ipe-login-compare__batch-name">{item.name}</span>
                <span className="ipe-login-compare__batch-status">
                  {item.status === 'done' ? t('compare.done') : t('compare.listing')}
                </span>
              </li>
            ))}
          </ul>
          <div className="ipe-login-compare__batch-bar">
            <span className="ipe-login-compare__batch-bar-fill" style={{ width: `${ai.progress}%` }} />
          </div>
          <p className="ipe-login-compare__batch-progress">{t('compare.progress', { percent: ai.progress })}</p>
        </PreviewShell>
      );

    case 'sync':
      return (
        <PreviewShell ai={ai} onAction={onAction} className="ipe-login-compare__preview--sync">
          <ul className="ipe-login-compare__sync-list">
            {ai.platforms.map((platform) => (
              <li key={platform.name} className="ipe-login-compare__sync-item">
                <span className="ipe-login-compare__sync-icon" style={{ background: platform.color }}>
                  {platform.name.slice(0, 1)}
                </span>
                <div className="ipe-login-compare__sync-info">
                  <strong>{platform.name}</strong>
                  <span>{platform.status}</span>
                </div>
              </li>
            ))}
          </ul>
        </PreviewShell>
      );

    default:
      return null;
  }
}

const WORKFLOW_STEPS = [
  {
    id: 'upload',
    index: '01',
    title: '筛选爆款商品图片',
    desc: '快速锁定高转化的主图与详情图。',
  },
  {
    id: 'analyze',
    index: '02',
    title: 'AI 智能工作流',
    desc: '标题、套图和规格一次串起来。',
  },
  {
    id: 'generate',
    index: '03',
    title: '批量上架电商平台',
    desc: '一次配置，同步发到多个平台。',
  },
];

const ADVANTAGE_CARDS = [
  {
    id: 'templates',
    title: '模版库沉淀复用，高频场景开箱即用',
    points: [
      '套图模版按场景、款式、店铺分类管理',
      '上架模版覆盖规格、价格与文案字段配置',
      '团队共享调用，减少重复设计与反复排版',
    ],
    image: `${LOGIN_IMAGE_BASE}/advantage-templates.png`,
  },
  {
    id: 'pipeline',
    title: '一站式图像处理链路，效率提升 10 倍',
    points: [
      '标题提取、智能抠图、套图生成一条链路串联',
      '批量处理 SKU，无需在多个工具间来回切换',
      '任务进度实时可视，出图质量全程可监控',
    ],
    image: `${LOGIN_IMAGE_BASE}/advantage-pipeline.png`,
  },
  {
    id: 'platform',
    title: '多平台对接上架，规模化运营更轻松',
    points: [
      '货源平台、商品库与店铺数据一键同步',
      '上架模版沉淀复用，多店批量铺货不重复劳动',
      '工作流自动化编排，减少人工录入与差错',
    ],
    image: `${LOGIN_IMAGE_BASE}/advantage-platform.png`,
  },
  {
    id: 'ai-media',
    title: 'AI 视觉延展，商品内容更丰富',
    points: [
      '视频生成与模特试衣拓展商品展示维度',
      '图裂变、万能改图快速产出多风格素材',
      '处理结果自动写入图片空间，全链路可追溯',
    ],
    image: `${LOGIN_IMAGE_BASE}/advantage-ai-media.png`,
  },
];

const SHOWCASE_SLIDES = [
  {
    id: 'chair',
    title: '品质藤椅',
    desc: '纯白空间下的藤编肌理，AI 还原材质细节与柔和阴影',
    image: `${LOGIN_IMAGE_BASE}/showcase-chair.png`,
  },
  {
    id: 'coat',
    title: '毛呢大衣',
    desc: '都市街拍场景，女性模特佩戴墨镜，展现秋冬长款大衣质感',
    image: `${LOGIN_IMAGE_BASE}/showcase-coat-street.png`,
  },
  {
    id: 'pearl',
    title: '璀璨珍珠',
    desc: '柔光布景突出首饰光泽，达到电商主图级呈现标准',
    image: `${LOGIN_IMAGE_BASE}/showcase-pearl.png`,
  },
  {
    id: 'floral-dress',
    title: '法式碎花裙',
    desc: '影棚柔光布景，碎花连衣裙全身展示，电商模特图专业质感',
    image: `${LOGIN_IMAGE_BASE}/showcase-floral-dress.png`,
  },
  {
    id: 'perfume',
    title: '香氛主图',
    desc: '纯净白底与金属质感，一键生成符合平台规范的商品图',
    image: `${LOGIN_IMAGE_BASE}/showcase-perfume.png`,
  },
  {
    id: 'hiphop',
    title: '嘻哈街头外套',
    desc: '街头滑板场景，嘻哈风格外套动态展示，潮流感一键生成',
    image: `${LOGIN_IMAGE_BASE}/showcase-hiphop-skate.png`,
  },
  {
    id: 'mug',
    title: '陶瓷马克杯',
    desc: '套图模版批量出图，保持多 SKU 视觉风格统一',
    image: `${LOGIN_IMAGE_BASE}/showcase-mug.png`,
  },
  {
    id: 'dress',
    title: '连衣裙主图',
    desc: '柔光人像展示，适合详情页与主图切换',
    image: `${LOGIN_IMAGE_BASE}/showcase-dress.png`,
  },
  {
    id: 'shoes',
    title: '运动鞋主图',
    desc: '干净背景突出鞋型，适合多 SKU 套图',
    image: `${LOGIN_IMAGE_BASE}/showcase-shoes.png`,
  },
];

const FOOTER_LINK_GROUPS = [
  {
    id: 'image',
    title: '图片创作',
    links: ['智能抠图', '主图创作', '万能改图', '图裂变'],
  },
  {
    id: 'ai',
    title: 'AI 工具',
    links: ['标题提取', '超分提质', '侵权检测', '工作流编排'],
  },
  {
    id: 'ops',
    title: '运营上架',
    links: ['批量上架', '上架管理', '套图模版', '数据采集'],
  },
];

function useRevealOnScroll(threshold = 0.12) {
  const ref = useCallback((node) => {
    if (!node || node.dataset.revealBound === 'true') return;

    node.dataset.revealBound = 'true';

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('ipe-login-advantages__card--visible');
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -6% 0px' },
    );

    observer.observe(node);
  }, [threshold]);

  return ref;
}

function AdvantageCard({ card }) {
  const ref = useRevealOnScroll();

  return (
    <article ref={ref} className="ipe-login-advantages__card">
      <div className="ipe-login-advantages__card-media" aria-hidden="true">
        <img
          className="ipe-login-advantages__card-image"
          src={card.image}
          alt=""
          loading="lazy"
          draggable={false}
        />
      </div>
      <div className="ipe-login-advantages__card-body">
        <h3 className="ipe-login-advantages__card-title">{card.title}</h3>
        <ul className="ipe-login-advantages__card-points">
          {card.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function EffectShowcaseCarousel() {
  const { t } = useTranslation('login');
  const slides = useMemo(
    () => SHOWCASE_SLIDES.map((slide) => ({
      ...slide,
      title: t(`showcase.slides.${slide.id}.title`),
      desc: t(`showcase.slides.${slide.id}.desc`),
    })),
    [t],
  );
  const count = slides.length;
  const loopSlides = [...slides, ...slides];
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = window.setInterval(() => {
      setAnimate(true);
      setIndex((current) => current + 1);
    }, 3200);
    return () => window.clearInterval(timer);
  }, [paused]);

  useEffect(() => {
    if (animate || index !== 0) return undefined;
    const frame = window.requestAnimationFrame(() => setAnimate(true));
    return () => window.cancelAnimationFrame(frame);
  }, [animate, index]);

  const shift = (delta) => {
    setAnimate(true);
    setIndex((current) => {
      const next = current + delta;
      if (next < 0) return count - 1;
      return next;
    });
  };

  const handleTrackEnd = () => {
    if (index < count) return;
    setAnimate(false);
    setIndex(0);
  };

  return (
    <section className="ipe-login-section ipe-login-section--showcase" aria-label={t('showcase.aria')}>
      <div className="ipe-login-section__inner">
        <header className="ipe-login-showcase__head">
          <span className="ipe-login-showcase__badge">{t('showcase.badge')}</span>
          <h2 className="ipe-login-showcase__title">{t('showcase.title')}</h2>
          <p className="ipe-login-showcase__subtitle">
            {t('showcase.subtitle')}
          </p>
        </header>
      </div>

      <div
        className="ipe-login-showcase-carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <button
          type="button"
          className="ipe-login-showcase-carousel__nav ipe-login-showcase-carousel__nav--prev"
          onClick={() => shift(-1)}
          aria-label={t('showcase.prev')}
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <div className="ipe-login-showcase-carousel__viewport">
          <div
            className={`ipe-login-showcase-carousel__track${animate ? '' : ' ipe-login-showcase-carousel__track--instant'}`}
            style={{ transform: `translateX(calc(${-index} * (var(--showcase-card-width) + var(--showcase-gap))))` }}
            onTransitionEnd={handleTrackEnd}
          >
            {loopSlides.map((slide, slideIndex) => (
              <article
                key={`${slide.id}-${slideIndex}`}
                className="ipe-login-showcase-carousel__card"
              >
                <div className="ipe-login-showcase-carousel__media">
                  <img
                    className="ipe-login-showcase-carousel__media-image"
                    src={slide.image}
                    alt=""
                    loading="lazy"
                    draggable={false}
                  />
                </div>
                <div className="ipe-login-showcase-carousel__body">
                  <h3 className="ipe-login-showcase-carousel__card-title">{slide.title}</h3>
                  <p className="ipe-login-showcase-carousel__card-desc">{slide.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
        <button
          type="button"
          className="ipe-login-showcase-carousel__nav ipe-login-showcase-carousel__nav--next"
          onClick={() => shift(1)}
          aria-label={t('showcase.next')}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
        <div className="ipe-login-showcase-carousel__dots" role="tablist" aria-label={t('showcase.dots')}>
          {slides.map((slide, dotIndex) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-label={slide.title}
              aria-selected={index % count === dotIndex}
              className={`ipe-login-showcase-carousel__dot${index % count === dotIndex ? ' is-active' : ''}`}
              onClick={() => {
                setAnimate(true);
                setIndex(dotIndex);
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function HeroVisualCard() {
  const { t } = useTranslation('login');
  return (
    <div className="ipe-login-hero__collage">
      <p className="ipe-login-hero__visual-caption">{t('hero.visualCaption')}</p>
      <div className="ipe-login-hero__collage-stage">
        <img
          className="ipe-login-hero__shot ipe-login-hero__shot--mug"
          src={`${LOGIN_IMAGE_BASE}/showcase-mug.png`}
          alt=""
          draggable={false}
        />
        <img
          className="ipe-login-hero__shot ipe-login-hero__shot--dress"
          src={`${LOGIN_IMAGE_BASE}/showcase-floral-dress.png`}
          alt=""
          draggable={false}
        />
        <img
          className="ipe-login-hero__shot ipe-login-hero__shot--goods"
          src={`${LOGIN_IMAGE_BASE}/showcase-shoes.png`}
          alt=""
          draggable={false}
        />
        <div className="ipe-login-hero__cutout-card">
          <img src={`${LOGIN_IMAGE_BASE}/compare-cutout-before.png`} alt="" draggable={false} />
          <span aria-hidden="true">→</span>
          <img src={`${LOGIN_IMAGE_BASE}/compare-cutout-after.png`} alt="" draggable={false} />
        </div>
        <span className="ipe-login-hero__mins">5 mins</span>
      </div>
    </div>
  );
}

export default function IpeLoginShowcase({ onOpenLogin }) {
  const { t } = useTranslation('login');
  const [activeTab, setActiveTab] = useState(COMPARE_TABS[0].id);
  const tabs = useMemo(() => COMPARE_TABS.map((tab) => {
    const ai = { ...tab.ai, actionLabel: t(`compare.tabs.${tab.id}.action`) };
    if (tab.ai.badge) ai.badge = t(`compare.tabs.${tab.id}.badge`);
    if (tab.ai.stat) ai.stat = t(`compare.tabs.${tab.id}.stat`);
    if (tab.ai.titles) ai.titles = asList(t(`compare.tabs.${tab.id}.titles`, { returnObjects: true }));
    if (tab.ai.items) {
      const names = asList(t(`compare.tabs.${tab.id}.items`, { returnObjects: true }));
      ai.items = tab.ai.items.map((item, index) => ({ ...item, name: names[index] || item.name }));
    }
    if (tab.ai.platforms) {
      const platforms = asList(t(`compare.tabs.${tab.id}.platforms`, { returnObjects: true }));
      ai.platforms = tab.ai.platforms.map((platform, index) => ({
        ...platform,
        name: platforms[index]?.name || platform.name,
        status: platforms[index]?.status || platform.status,
      }));
    }
    return {
      ...tab,
      label: t(`compare.tabs.${tab.id}.label`),
      traditional: {
        ...tab.traditional,
        steps: asList(t(`compare.tabs.${tab.id}.steps`, { returnObjects: true })),
        footnote: t(`compare.tabs.${tab.id}.footnote`),
      },
      ai,
    };
  }), [t]);
  const workflowSteps = useMemo(
    () => WORKFLOW_STEPS.map((item, index) => ({
      ...item,
      title: t(`workflow.${index}.title`),
      desc: t(`workflow.${index}.desc`),
    })),
    [t],
  );
  const advantageCards = useMemo(
    () => ADVANTAGE_CARDS.map((card) => {
      const key = card.id === 'ai-media' ? 'aiMedia' : card.id;
      return {
        ...card,
        title: t(`advantages.cards.${key}.title`),
        points: asList(t(`advantages.cards.${key}.points`, { returnObjects: true })),
      };
    }),
    [t],
  );
  const footerGroups = useMemo(
    () => FOOTER_LINK_GROUPS.map((group) => ({
      ...group,
      title: t(`footer.groups.${group.id}.title`),
      links: asList(t(`footer.groups.${group.id}.links`, { returnObjects: true })),
    })),
    [t],
  );
  const heroChips = asList(t('hero.chips', { returnObjects: true }));
  const current = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  return (
    <>
      <section className="ipe-login-section ipe-login-section--hero" aria-label={t('hero.aria')}>
        <div className="ipe-login-section__inner ipe-login-hero-layout">
          <div className="ipe-login-hero__content">
            <div className="ipe-login-hero__text-block">
              <h1 className="ipe-login-hero__title">
                {t('hero.titleBefore')} <span className="ipe-login-accent">10</span> {t('hero.titleTimes')}
                <br />
                {t('hero.titleLine2')}
              </h1>

              <p className="ipe-login-hero__desc">
                {t('hero.desc')}
              </p>
            </div>

            <div className="ipe-login-hero__cta">
              <button
                type="button"
                className="ipe-login-hero__cta-btn ipe-login-hero__cta-btn--primary"
                onClick={onOpenLogin}
              >
                {t('hero.cta')}
                <span className="ipe-login-hero__cta-arrow" aria-hidden="true">→</span>
              </button>
            </div>
            <ul className="ipe-login-hero__chips">
              {heroChips.map((chip) => (
                <li key={chip}>{chip}</li>
              ))}
            </ul>
          </div>

          <div className="ipe-login-hero__visual" aria-hidden="true">
            <HeroVisualCard />
          </div>
        </div>
      </section>

      <section className="ipe-login-section ipe-login-section--compare" aria-label={t('compare.aria')}>
        <div className="ipe-login-section__inner">
          <div className="ipe-login-compare">
            <h2 className="ipe-login-compare__title">
              {t('compare.titleBefore')} <span className="ipe-login-accent">20</span> {t('compare.titleAfter')}
            </h2>
            <p className="ipe-login-compare__subtitle">
              {t('compare.subtitle')}
            </p>

            <div className="ipe-login-compare__tabs" role="tablist" aria-label={t('compare.tabsAria')}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`ipe-login-compare__tab${activeTab === tab.id ? ' ipe-login-compare__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="ipe-login-compare__panels">
              <article className="ipe-login-compare__panel ipe-login-compare__panel--traditional">
                <header className="ipe-login-compare__panel-head">
                  <span className="ipe-login-compare__panel-label">{t('compare.traditional')}</span>
                  <strong className="ipe-login-compare__panel-time">{current.traditional.time}</strong>
                </header>
                <ul className="ipe-login-compare__steps">
                  {current.traditional.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ul>
                <footer className="ipe-login-compare__footnote">
                  <Sparkles size={15} aria-hidden="true" />
                  <span>{current.traditional.footnote}</span>
                </footer>
              </article>

              <article className="ipe-login-compare__panel ipe-login-compare__panel--ai">
                <header className="ipe-login-compare__panel-head">
                  <span className="ipe-login-compare__brand">
                    <IpeBrandLogo size={32} className="ipe-login-compare__brand-icon" alt="" />
                    {t('compare.engine')}
                  </span>
                  <strong className="ipe-login-compare__panel-time ipe-login-compare__panel-time--accent">
                    {current.ai.time}
                  </strong>
                </header>

                <AiPreview key={activeTab} ai={current.ai} onAction={onOpenLogin} />
              </article>
            </div>
          </div>

          <div className="ipe-login-workflow" aria-label={t('compare.workflowAria')}>
            <header className="ipe-login-workflow__head">
              <h2 className="ipe-login-workflow__title">{t('compare.workflowTitle')}</h2>
              <p className="ipe-login-workflow__subtitle">
                {t('compare.workflowSubtitle')}
              </p>
            </header>

            <div className="ipe-login-workflow__steps">
              {workflowSteps.map((item) => (
                <article key={item.id} className="ipe-login-workflow__step">
                  <span className="ipe-login-workflow__index">{item.index}</span>
                  <h3 className="ipe-login-workflow__step-title">{item.title}</h3>
                  <p className="ipe-login-workflow__step-desc">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ipe-login-section ipe-login-section--advantages" aria-label={t('advantages.aria')}>
        <div className="ipe-login-section__inner ipe-login-advantages">
          <div className="ipe-login-advantages__aside-col">
            <aside className="ipe-login-advantages__sticky">
              <h2 className="ipe-login-advantages__title">
                {t('advantages.titleLine1')}
                <br />
                {t('advantages.titleLine2')}
              </h2>
              <p className="ipe-login-advantages__desc">
                {t('advantages.desc')}
              </p>
              <button
                type="button"
                className="ipe-login-advantages__cta"
                onClick={onOpenLogin}
              >
                {t('advantages.cta')}
              </button>
            </aside>
          </div>

          <div className="ipe-login-advantages__cards">
            {advantageCards.map((card) => (
              <AdvantageCard key={card.id} card={card} />
            ))}
          </div>
        </div>
      </section>

      <EffectShowcaseCarousel />

      <footer className="ipe-login-footer" aria-label={t('footer.aria')}>
        <section className="ipe-login-footer__cta" aria-label={t('footer.ctaAria')}>
          <div className="ipe-login-section__inner ipe-login-footer__cta-inner">
            <h2 className="ipe-login-footer__cta-title">
              {t('footer.ctaTitle')}
            </h2>
            <button
              type="button"
              className="ipe-login-footer__cta-btn"
              onClick={onOpenLogin}
            >
              {t('footer.cta')}
            </button>
          </div>
        </section>

        <div className="ipe-login-footer__main">
          <div className="ipe-login-section__inner">
            <div className="ipe-login-footer__grid">
              <div className="ipe-login-footer__brand">
                <IpeBrandMark
                  className="ipe-login-footer__logo"
                  textClassName="ipe-login-footer__logo-text"
                  text="IPE"
                />
                <p className="ipe-login-footer__tagline">
                  {t('footer.tagline')}
                </p>
              </div>

              {footerGroups.map((group) => (
                <nav key={group.id} className="ipe-login-footer__col" aria-label={group.title}>
                  <h3 className="ipe-login-footer__col-title">{group.title}</h3>
                  <ul className="ipe-login-footer__links">
                    {group.links.map((link) => (
                      <li key={link}>
                        <button
                          type="button"
                          className="ipe-login-footer__link"
                          onClick={onOpenLogin}
                        >
                          {link}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              ))}
            </div>

            <div className="ipe-login-footer__icp">
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noopener noreferrer"
                className="ipe-login-footer__icp-link"
              >
                粤ICP备2026080153号-1
              </a>
              <span className="ipe-login-footer__icp-sep" aria-hidden="true">|</span>
              <a
                href="https://www.beian.gov.cn/portal/registerSystemInfo?recordcode=44011102485003"
                target="_blank"
                rel="noopener noreferrer"
                className="ipe-login-footer__icp-link"
              >
                粤公网安备44011102485003号
              </a>
              <span className="ipe-login-footer__icp-sep" aria-hidden="true">|</span>
              <span className="ipe-login-footer__icp-brand">2026云创科技</span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
